import { useEffect, useState } from "react";
import { formatChinaDateTime } from "../time";

import {
  confirmExceptionNoData,
  confirmExceptionTimeout,
  getExceptionOrder,
  listExceptionOrders,
  requeryExceptionTask,
  type ExceptionInterface,
  type ExceptionOrder,
  type ExceptionOrderDetail,
} from "./api";

const PAGE_SIZE = 10;

type PendingAction = {
  kind: "requery" | "timeout" | "no-data";
  item: ExceptionInterface;
} | null;

const STATUS_NAMES: Record<string, string> = {
  SUCCEEDED: "正常",
  EXPLICIT_FAILURE: "查询失败",
  TIMEOUT_UNKNOWN: "状态待确认",
  RUNNING: "补查中",
  PENDING: "等待查询",
};

function totalPages(total: number): number {
  return Math.max(1, Math.ceil(total / PAGE_SIZE));
}

export function ExceptionOrders() {
  const [bucket, setBucket] = useState<"pending" | "completed">("pending");
  const [orders, setOrders] = useState<ExceptionOrder[]>([]);
  const [productCode, setProductCode] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [draftQuery, setDraftQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState<ExceptionOrderDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [note, setNote] = useState("");
  const [isBilled, setIsBilled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState("");
  const [toast, setToast] = useState("");

  async function reload() {
    setLoading(true);
    setError("");
    try {
      const result = await listExceptionOrders({ bucket, query, productCode, page, pageSize: PAGE_SIZE });
      setOrders(result.items);
      setTotal(result.total);
      if (page > totalPages(result.total)) setPage(totalPages(result.total));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "异常订单加载失败");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); }, [bucket, page, productCode, query]);
  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  async function openOrder(orderId: string) {
    setDetailLoading(true);
    setModalError("");
    try {
      setDetail(await getExceptionOrder(orderId));
    } catch (reason) {
      setModalError(reason instanceof Error ? reason.message : "异常详情加载失败");
    } finally {
      setDetailLoading(false);
    }
  }

  function openAction(kind: NonNullable<PendingAction>["kind"], item: ExceptionInterface) {
    setPendingAction({ kind, item });
    setNote("");
    setIsBilled(false);
    setModalError("");
  }

  async function submitAction() {
    if (!pendingAction?.item.task_id || !detail) return;
    if (pendingAction.kind !== "requery" && !note.trim()) {
      setModalError("请填写人工确认说明");
      return;
    }
    setSubmitting(true);
    setModalError("");
    try {
      const key = crypto.randomUUID();
      const result = pendingAction.kind === "requery"
        ? await requeryExceptionTask(pendingAction.item.task_id, key)
        : pendingAction.kind === "timeout"
          ? await confirmExceptionTimeout(pendingAction.item.task_id, {
              isBilled,
              note: note.trim(),
              idempotencyKey: key,
            })
          : await confirmExceptionNoData(pendingAction.item.task_id, note.trim(), key);
      setPendingAction(null);
      setToast(result.message);
      await openOrder(detail.order_id);
      await reload();
    } catch (reason) {
      setModalError(reason instanceof Error ? reason.message : "处理失败");
    } finally {
      setSubmitting(false);
    }
  }

  function switchBucket(value: "pending" | "completed") {
    setBucket(value);
    setPage(1);
  }

  return (
    <section className="platform-subaccounts platform-exception-orders" aria-label="异常订单">
      <div className="platform-panel">
        <div className="platform-order-tabs" role="tablist" aria-label="异常订单状态">
          <button className={bucket === "pending" ? "is-active" : ""} type="button" onClick={() => switchBucket("pending")}>待处理</button>
          <button className={bucket === "completed" ? "is-active" : ""} type="button" onClick={() => switchBucket("completed")}>已完成</button>
        </div>
        <form className="platform-list-toolbar" onSubmit={(event) => { event.preventDefault(); setPage(1); setQuery(draftQuery.trim()); }}>
          <div className="platform-list-search">
            <input aria-label="搜索异常订单" placeholder="搜索机构或查询对象" value={draftQuery} onChange={(event) => setDraftQuery(event.target.value)} />
            <button type="submit">查询</button>
            <select aria-label="筛选异常产品" value={productCode} onChange={(event) => { setProductCode(event.target.value); setPage(1); }}>
              <option value="">全部产品</option><option value="NATURAL_PERSON">自然人综合背景协查</option><option value="ENTERPRISE_OPERATION">企业经营信息查询</option><option value="OWNED_VEHICLE">名下车辆协查</option><option value="VEHICLE_DETAIL">指定车辆信息协查</option>
            </select>
          </div>
        </form>
        <div className="platform-table-wrap">
          <table className="platform-account-table platform-exception-table">
            <thead><tr><th>订单查询时间</th><th>订单编号</th><th>结果编号</th><th>机构</th><th>服务 / 查询对象</th><th>订单状态</th><th>操作</th></tr></thead>
            <tbody>
              {!loading && !error && orders.map((order) => <tr key={order.order_id}>
                <td>{formatChinaDateTime(order.created_at)}</td>
                <td><strong>{order.order_number}</strong></td>
                <td>{order.result_number || "暂未生成"}</td>
                <td>{order.institution_name || "—"}</td>
                <td><strong>{order.product_name}</strong><br /><span>{order.subject_summary}</span></td>
                <td>{order.order_status}</td>
                <td><div className="platform-row-actions"><button type="button" onClick={() => void openOrder(order.order_id)}>查看处理</button></div></td>
              </tr>)}
              {!loading && !error && Array.from({ length: Math.max(0, PAGE_SIZE - Math.max(orders.length, 1)) }, (_, index) => (
                <tr aria-hidden="true" className="platform-exception-empty-row" key={`empty-${index}`}><td colSpan={7}>&nbsp;</td></tr>
              ))}
              {loading && <tr><td className="platform-table-state" colSpan={7}>正在加载订单…</td></tr>}
              {!loading && error && <tr><td className="platform-table-state is-error" colSpan={7}>{error}<button type="button" onClick={() => void reload()}>重新加载</button></td></tr>}
              {!loading && !error && orders.length === 0 && <tr><td className="platform-table-state" colSpan={7}>{bucket === "pending" ? "暂无待处理异常订单" : "暂无已完成异常订单"}</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="platform-pagination"><span>共 {total} 条，每页 {PAGE_SIZE} 条</span><div><button type="button" disabled={page <= 1 || loading} onClick={() => setPage((value) => value - 1)}>上一页</button><span>{page} / {totalPages(total)}</span><button type="button" disabled={page >= totalPages(total) || loading} onClick={() => setPage((value) => value + 1)}>下一页</button></div></div>
      </div>

      {(detail || detailLoading) && <div className="platform-modal-layer" role="presentation">
        <section className="platform-modal platform-exception-modal" role="dialog" aria-modal="true" aria-labelledby="exception-order-title">
          <div className="platform-modal-heading"><h2 id="exception-order-title">异常订单处理</h2><button aria-label="关闭" type="button" onClick={() => setDetail(null)}>×</button></div>
          {detailLoading && !detail && <p className="platform-exception-loading">正在加载异常详情…</p>}
          {detail && <>
            <dl className="platform-property-order-summary"><div><dt>订单编号</dt><dd>{detail.order_number}</dd></div><div><dt>结果编号</dt><dd>{detail.result_number || "暂未生成"}</dd></div><div><dt>机构</dt><dd>{detail.institution_name || "—"}</dd></div><div><dt>服务</dt><dd>{detail.product_name}</dd></div><div><dt>查询对象</dt><dd>{detail.subject_summary}</dd></div><div><dt>客户侧计费状态</dt><dd>{detail.customer_billing_status}</dd></div></dl>
            <p className="platform-exception-rule">每次仅补查所选异常接口；已成功接口不会重复查询，也不会重复计费。</p>
            <div className="platform-exception-interface-list">
              {detail.interfaces.map((item) => <article className={item.status === "SUCCEEDED" ? "is-success" : "is-abnormal"} key={item.interface_code}>
                <div className="platform-exception-interface-main">
                  <div><strong>{item.interface_name}</strong><span>{item.interface_code} · {item.provider_name}</span></div>
                  <span className={`platform-call-status is-${item.status.toLowerCase()}`}>{STATUS_NAMES[item.status] ?? item.status}</span>
                  <p>{item.resolution_kind === "INPUT_CORRECTION_REQUIRED" ? "需更正信息：供应商已明确提示输入格式或参数有误，请更正后重新发起订单。" : <>{item.error_code ? `${item.error_code} · ` : ""}{item.error_summary || (item.status === "SUCCEEDED" ? "接口已正常返回" : "等待人工处理")}</>}</p>
                  <small>已补查 {item.supplement_count}/{item.supplement_limit} 次</small>
                </div>
                <div className="platform-exception-actions">
                  {item.requires_timeout_confirmation && <button type="button" onClick={() => openAction("timeout", item)}>确认原调用状态</button>}
                  {item.can_requery && <button type="button" onClick={() => openAction("requery", item)}>补查此接口</button>}
                  {item.can_confirm_no_data && <button className="is-secondary" type="button" onClick={() => openAction("no-data", item)}>确认未查得</button>}
                  {!item.requires_timeout_confirmation && !item.can_requery && !item.can_confirm_no_data && <span>{item.status === "SUCCEEDED" ? "无需处理" : "已达到处理上限"}</span>}
                </div>
                <details><summary>查看调用记录（{item.history.length}）</summary><ol>{item.history.map((call) => <li key={call.id}><div className="platform-exception-history-head"><span>{call.is_supplement ? `第 ${call.batch_no - 1} 次补查` : "首次查询"}</span><span>{STATUS_NAMES[call.status] ?? call.status}</span><span>{call.is_billed === null ? "供应商计费待确认" : call.is_billed ? "供应商已计费" : "供应商未计费"}</span><time>{formatChinaDateTime(call.created_at)}</time></div><div className="platform-exception-history-detail"><span>供应商请求编号：{call.evidence.provider_request_id || call.provider_request_id || "暂未返回"}</span><span>响应状态：{call.evidence.http_status ? `HTTP ${call.evidence.http_status}` : "未收到 HTTP 响应"} · {call.evidence.response_received ? "已收到回包" : "未收到回包"} · 耗时 {call.evidence.duration_ms ?? call.duration_ms ?? "—"}ms</span><span>接口路径：{call.evidence.endpoint_path || "暂未留存"}</span></div><details><summary>原始回包</summary>{call.evidence.available ? <pre>{JSON.stringify(call.evidence.response_body, null, 2)}</pre> : <p>{call.evidence.reason}</p>}</details></li>)}</ol></details>
              </article>)}
            </div>
          </>}
          {modalError && <p className="platform-modal-error">{modalError}</p>}
        </section>
      </div>}

      {pendingAction && <div className="platform-modal-layer platform-confirm-layer" role="presentation">
        <section className="platform-modal platform-confirm-modal" role="alertdialog" aria-modal="true" aria-labelledby="exception-action-title">
          <div className="platform-modal-heading"><h2 id="exception-action-title">{pendingAction.kind === "requery" ? "确认补查此接口" : pendingAction.kind === "timeout" ? "确认原调用状态" : "确认接口未查得"}</h2></div>
          <p><strong>{pendingAction.item.interface_code}</strong></p>
          {pendingAction.kind === "requery" && <p>本次只重新查询这个异常接口，不会调用其他已成功接口。确认发起后可能产生一次供应商费用。</p>}
          {pendingAction.kind === "timeout" && <label>原调用是否计费<select value={isBilled ? "yes" : "no"} onChange={(event) => setIsBilled(event.target.value === "yes")}><option value="no">未计费</option><option value="yes">已计费</option></select></label>}
          {pendingAction.kind !== "requery" && <label>人工确认说明<textarea value={note} onChange={(event) => setNote(event.target.value)} placeholder="请填写与供应商核验后的确认情况" /></label>}
          {modalError && <p className="platform-modal-error">{modalError}</p>}
          <div className="platform-modal-actions"><button className="is-secondary" type="button" disabled={submitting} onClick={() => setPendingAction(null)}>取消</button><button className="platform-primary-button" type="button" disabled={submitting} onClick={() => void submitAction()}>{submitting ? "处理中…" : "确认"}</button></div>
        </section>
      </div>}

      {toast && <div className="platform-toast" role="status">{toast}</div>}
    </section>
  );
}
